// Pure math for the Overall Performance trend block (readiness design
// docs/qlearn-performance-readiness-design.md, slice 1). Everything takes an
// explicit `now` so windows are deterministic and unit-testable.
//
// Accuracy is QUESTION-WEIGHTED (sum of correct over sum of questions), not
// quiz-averaged: a 5-question 100% must not swamp a 40-question 60%.

export const MS_PER_DAY = 86400000;
export const WINDOW_DAYS = 14;
export const TREND_SPAN_DAYS = 90;
export const MIN_QUIZZES_FOR_TREND = 2;
export const ALL_EXAMS = "__all__";

// One quiz-history row -> one trend point, or null when the row can't carry
// trend weight (missing/invalid date, size, or score). Dropping bad rows
// beats plotting a fabricated point.
//
// Backend notes (#520 gate, verified against QuizHistoryService projection):
// - `total` is score.attempt_count — questions ATTEMPTED, not the quiz's
//   questionCount. For question-weighted accuracy that is the right
//   denominator.
// - In-progress quizzes can appear in the list with a score row; they are
//   excluded explicitly via `iscomplete`, not just by a missing date.
// - Raw `correct` rides the wire too — used directly when present so the
//   weighting needs no reconstruction from the rounded percentage.
export const parseQuizPoint = (row) => {
  if (row?.iscomplete === false) {
    return null;
  }

  const time = new Date(row?.complete ?? NaN).getTime();
  const total = Number(row?.total);
  const pct = Number(row?.percentageCorrect);

  if (!Number.isFinite(time) || !Number.isFinite(total) || total <= 0 || !Number.isFinite(pct)) {
    return null;
  }

  // G1 (#522 gate): a corrupt wire count (e.g. correct=999 of total=45) or an
  // out-of-range percentage must never mint impossible accuracy. The wire
  // value is used only when it agrees with [0, total]; otherwise fall back to
  // derivation, and clamp the final count into [0, total] regardless.
  const wireCorrect = Number(row?.correct);
  const derivedCorrect = Math.round((pct / 100) * total);
  const candidateCorrect =
    Number.isFinite(wireCorrect) && wireCorrect >= 0 && wireCorrect <= total
      ? wireCorrect
      : derivedCorrect;

  return {
    time,
    total,
    pct,
    correct: Math.min(Math.max(candidateCorrect, 0), total),
    examTypes: Array.isArray(row?.category?.examType) ? row.category.examType : [],
    disciplines: Array.isArray(row?.category?.discipline) ? row.category.discipline : [],
    // Q3 (readiness basis exclusion): a quiz taken with tutor mode on, or one
    // assigned by a tutor, is not unaided practice. The TREND still plots
    // these — only the readiness math filters on this flag.
    tutorAssisted: row?.tutorOn === true || !!row?.assignedByTutorId,
    title: row?.title || ""
  };
};

export const toTrendPoints = (rows) =>
  Array.isArray(rows)
    ? rows.map(parseQuizPoint).filter(Boolean).sort((a, b) => a.time - b.time)
    : [];

export const listExamTypes = (points) => {
  const seen = new Set();
  points.forEach((point) => point.examTypes.forEach((examType) => seen.add(examType)));
  return [...seen].sort((a, b) => a.localeCompare(b));
};

export const filterByExamType = (points, examType) =>
  !examType || examType === ALL_EXAMS
    ? points
    : points.filter((point) => point.examTypes.includes(examType));

// The student's dominant exam type across the given points: highest total
// question count (weight, not quiz count); ties broken by most recent
// activity. "" when no point carries an exam type.
export const dominantExamType = (points) => {
  const weight = new Map();
  const latest = new Map();

  points.forEach((point) => {
    point.examTypes.forEach((examType) => {
      weight.set(examType, (weight.get(examType) || 0) + point.total);
      latest.set(examType, Math.max(latest.get(examType) || 0, point.time));
    });
  });

  let best = "";
  weight.forEach((questions, examType) => {
    if (
      !best ||
      questions > weight.get(best) ||
      (questions === weight.get(best) && latest.get(examType) > latest.get(best))
    ) {
      best = examType;
    }
  });

  return best;
};

// Question-weighted stats over (end - days, end], where end = now minus
// offsetDays. accuracy is null (not 0) when the window holds no questions.
export const windowStats = (points, now, days = WINDOW_DAYS, offsetDays = 0) => {
  const end = now - offsetDays * MS_PER_DAY;
  const start = end - days * MS_PER_DAY;
  const inWindow = points.filter((point) => point.time > start && point.time <= end);
  const questions = inWindow.reduce((sum, point) => sum + point.total, 0);
  const correct = inWindow.reduce((sum, point) => sum + point.correct, 0);

  return {
    quizzes: inWindow.length,
    questions,
    // raw sum exposed so verdict-class consumers can gate on the UNROUNDED
    // ratio (F1, #522 gate); `accuracy` is display-rounded.
    correct,
    accuracy: questions > 0 ? Math.round((correct / questions) * 100) : null
  };
};

// Current trailing window vs the one before it. delta is null unless both
// windows hold data — "up 7 pts vs nothing" is not a claim we make.
// deltaExact is the unrounded difference for verdict gating (F1, #522 gate:
// rounding 74.5→75 and a rounded delta of 0 must never turn
// below-target-and-falling into a top verdict); `delta` rounds it for display.
export const trendDelta = (points, now, days = WINDOW_DAYS) => {
  const current = windowStats(points, now, days, 0);
  const prior = windowStats(points, now, days, days);
  const exact = (window) =>
    window.questions > 0 ? (window.correct / window.questions) * 100 : null;
  const currentExact = exact(current);
  const priorExact = exact(prior);
  const deltaExact =
    currentExact !== null && priorExact !== null ? currentExact - priorExact : null;

  return {
    current,
    prior,
    deltaExact,
    delta: deltaExact !== null ? Math.round(deltaExact) : null
  };
};
